import { ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApprConfigService } from './appr-config.service';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import * as _ from 'lodash-es';
import { forkJoin, Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-appr-config',
    templateUrl: './appr-config.component.html',
    styleUrls: ['./appr-config.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ApprConfigComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();
  private readonly subs = new Subscription();

  // Standard component properties
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;

  // Component data properties
  rowData: any = {};
  filterParams: any = {};
  globalService: any = {};
  USER_SETTINGS: any = {};
  GENERAL_CONFIG: any = {};
  SERVER_CONFIG: any = {};

  // Modal properties
  modalTitle = "Appr Configuration";

  // Form data
  grossIncome: any = {
    GIE_NAME: '',
    GIE_SHORT_DESC: 'SELECT'
  };
  grossIncome_backup: any = {};

  filerEntity: any[] = []; // For add mode (multiselect) - kept for backward compatibility
  selectedFilerEntityKeys: string[] = []; // For multi-select binding (keys as strings)
  filerEntityEdit: any = {}; // For edit mode (single select)
  filerEntity_backup: any[] = [];
  filerEntityEdit_backup: any = {};

  status: any = {
    CODE_GROUP: "",
    IS_LOCKED: "SELECT",
    LOCK_DESC: ""
  };
  status_backup: any = {};

  // Dropdown options
  grossIncomeDetails: any[] = [];
  filingAllDetails: any[] = [];
  filingDetails: any[] = [];
  filingEnityDetails: any[] = [];
  statusDetails: any[] = [];

  // Existing data for duplicate checking
  gotExistingData: any[] = [];

  // Edit mode flag
  isCreate = true;

  // User settings
  logged_in_user: string = '';

  // Searchable multi-select dropdown properties
  filerEntityDropdownOpen: boolean = false;
  filerEntitySearchText: string = '';
  filerEntityFilteredData: any[] = [];

  // Initialization flags
  private isInitialized = false;
  private isLoading = false;

  baseURLs: any = {
    admin: '',
    api: '',
    custom: '',
    efile: '',
    pdfEngine: '',
    bulkPdfEngine: ''
  };

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private apprConfigService: ApprConfigService,
    private jsonObjectFactory: JsonObjectFactoryService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
  }

  // Gate function to prevent duplicate initialization
  private tryInit(): void {
    if (this.isInitialized || this.isLoading) {
      return;
    }

    if (!this.globalService || !this.GENERAL_CONFIG || !this.filterParams || Object.keys(this.filterParams).length === 0) {
      return;
    }

    this.isInitialized = true;
    this.init();
  }

  init(): void {
    try {
      // Determine if create or edit mode
      // Check if rowData is empty or doesn't have ADJ_KEY
      this.isCreate = !this.rowData || Object.keys(this.rowData).length === 0;
      
      if (!this.isCreate) {
        this.modalTitle = "Edit Appr Configuration";
      } else {
        this.modalTitle = "Appr Configuration";
      }

      // Get logged in user
      if (this.USER_SETTINGS?.user?.sso_id) {
        this.logged_in_user = this.USER_SETTINGS.user.sso_id;
      }

      // Load initial data
      this.loadInitialData();
    } catch (e) {
      console.error('Error in component init:', e);
      this.showFormLoader = false;
      this.cdr.detectChanges();
    }
  }

  loadInitialData(): void {
    this.isLoading = true;
    this.showFormLoader = true;

    // Load gross income details - action_code: f69od7
    const params1: any = {
      action_code: 'f69od7',
      tax_year: this.filterParams?.tax_year || this.globalService?.tax_year || this.rowData?.TAX_YEAR || '',
      scenario: this.filterParams?.scenario || this.globalService?.scenario || this.rowData?.SCENARIO || '74',
      jcd_key: this.filterParams?.jcd_key || this.globalService?.jcd_key || this.rowData?.JCD_KEY || '250',
      jcd_ta_key: this.filterParams?.jcd_ta_key || this.globalService?.jcd_ta_key || '',
      activity_key: this.filterParams?.activity_key || this.globalService?.activity_key || '1349',
      screen_key: this.filterParams?.screen_key || this.globalService?.screen_key || '10765',
      screen_type: this.filterParams?.screen_type || this.globalService?.screen_type || 'grid',
      is_locked: this.filterParams?.is_locked || this.globalService?.is_locked || 'N',
      issue_id: this.filterParams?.issue_id || this.globalService?.issue_id || '',
      issue_short_desc: this.filterParams?.issue_short_desc || this.globalService?.issue_short_desc || '',
      issue_id_for_tx_save: this.filterParams?.issue_id_for_tx_save || this.globalService?.issue_id_for_tx_save || '',
      scenarioCode: this.filterParams?.scenarioCode || this.filterParams?.scenario_code || this.globalService?.scenarioCode || this.globalService?.scenario_code || 'RAF',
      is_issue_key_enabled: this.filterParams?.is_issue_key_enabled || this.globalService?.is_issue_key_enabled || 'N',
      filing_group_key: this.filterParams?.filing_group_key || this.globalService?.filing_group_key || '1'
    };

    // Load existing data - action_code: a83w9b
    const params2: any = {
      action_code: 'a83w9b',
      tax_year: this.filterParams?.tax_year || this.globalService?.tax_year || this.rowData?.TAX_YEAR || '',
      scenario: this.filterParams?.scenario || this.globalService?.scenario || this.rowData?.SCENARIO || '74',
      jcd_key: this.filterParams?.jcd_key || this.globalService?.jcd_key || this.rowData?.JCD_KEY || '250',
      jcd_ta_key: this.filterParams?.jcd_ta_key || this.globalService?.jcd_ta_key || '',
      activity_key: this.filterParams?.activity_key || this.globalService?.activity_key || '1349',
      screen_key: this.filterParams?.screen_key || this.globalService?.screen_key || '10765',
      screen_type: this.filterParams?.screen_type || this.globalService?.screen_type || 'grid',
      is_locked: this.filterParams?.is_locked || this.globalService?.is_locked || 'N',
      issue_id: this.filterParams?.issue_id || this.globalService?.issue_id || '',
      issue_short_desc: this.filterParams?.issue_short_desc || this.globalService?.issue_short_desc || '',
      issue_id_for_tx_save: this.filterParams?.issue_id_for_tx_save || this.globalService?.issue_id_for_tx_save || '',
      scenarioCode: this.filterParams?.scenarioCode || this.filterParams?.scenario_code || this.globalService?.scenarioCode || this.globalService?.scenario_code || 'RAF',
      is_issue_key_enabled: this.filterParams?.is_issue_key_enabled || this.globalService?.is_issue_key_enabled || 'N',
      filing_group_key: this.filterParams?.filing_group_key || this.globalService?.filing_group_key || '1'
    };

    forkJoin([
      this.jsonObjectFactory.getJSONObj(this.GENERAL_CONFIG.base_url + '/loadJsonObject', params1, this.globalService, null, null),
      this.jsonObjectFactory.getJSONObj(this.GENERAL_CONFIG.base_url + '/loadJsonObject', params2, this.globalService, null, null)
    ]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      ([grossIncomeResponse, existingDataResponse]: any[]) => {
        if (grossIncomeResponse.callSuccess === '1' && grossIncomeResponse.jsonObject) {
          this.grossIncomeDetails = grossIncomeResponse.jsonObject;
          this.grossIncomeDetails.unshift({
            GIE_NAME: '',
            GIE_SHORT_DESC: 'SELECT'
          });

          // Set initial gross income value
          if (!this.isCreate && this.rowData.GIE_SHORT_DESC) {
            const matched = this.grossIncomeDetails.find(item => item.GIE_SHORT_DESC === this.rowData.GIE_SHORT_DESC);
            if (matched) {
              this.grossIncome = matched;
              this.grossIncome_backup = JSON.parse(JSON.stringify(matched));
              // Load dependent data - matching AngularJS pattern: getFillingGroupInfo() then getStatus()
              this.getFillingGroupInfo();
              this.getStatus();
            }
          } else {
            this.grossIncome = {
              GIE_NAME: '',
              GIE_SHORT_DESC: 'SELECT'
            };
            this.grossIncome_backup = JSON.parse(JSON.stringify(this.grossIncome));
          }
        }

        if (existingDataResponse.callSuccess === '1' && existingDataResponse.jsonObject) {
          this.gotExistingData = existingDataResponse.jsonObject;
        }

        this.showFormLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading initial data:', error);
        this.showFormLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  getFillingGroupInfo(): void {
    if (!this.grossIncome || !this.grossIncome.GIE_NAME) {
      return;
    }

    // 🔴 CRITICAL: Include ALL parameters with proper fallbacks (matching action code 35gc5l)
    const params: any = {
      action_code: '35gc5l',
      activity_key: this.filterParams?.activity_key || this.globalService?.activity_key || this.rowData?.ACTIVITY_KEY || '1349',
      filing_group_key: this.filterParams?.filing_group_key || this.globalService?.filing_group_key || this.rowData?.FILING_GROUP_KEY || '1',
      gie_name: this.grossIncome.GIE_NAME,
      is_issue_key_enabled: this.filterParams?.is_issue_key_enabled || this.globalService?.is_issue_key_enabled || 'N',
      is_locked: this.filterParams?.is_locked || this.globalService?.is_locked || this.rowData?.IS_LOCKED || 'N',
      issue_id: this.filterParams?.issue_id || this.globalService?.issue_id || this.rowData?.ISSUE_ID || '',
      issue_id_for_tx_save: this.filterParams?.issue_id_for_tx_save || this.globalService?.issue_id_for_tx_save || this.rowData?.ISSUE_ID_FOR_TX_SAVE || '',
      issue_short_desc: this.filterParams?.issue_short_desc || this.globalService?.issue_short_desc || this.rowData?.ISSUE_SHORT_DESC || '',
      jcd_key: this.filterParams?.jcd_key || this.globalService?.jcd_key || this.rowData?.JCD_KEY || '250',
      jcd_ta_key: this.filterParams?.jcd_ta_key || this.globalService?.jcd_ta_key || this.rowData?.JCD_TA_KEY || '1250',
      scenario: this.filterParams?.scenario || this.globalService?.scenario || this.rowData?.SCENARIO || '74',
      scenarioCode: this.filterParams?.scenarioCode || this.filterParams?.scenario_code || this.globalService?.scenarioCode || this.globalService?.scenario_code || 'RAF',
      screen_key: this.filterParams?.screen_key || this.globalService?.screen_key || this.rowData?.SCREEN_KEY || '10765',
      screen_type: this.filterParams?.screen_type || this.globalService?.screen_type || this.rowData?.SCREEN_TYPE || 'grid',
      tax_year: this.filterParams?.tax_year || this.globalService?.tax_year || this.rowData?.TAX_YEAR || ''
    };

    this.jsonObjectFactory.getJSONObj(this.GENERAL_CONFIG.base_url + '/loadJsonObject', params, this.globalService, null, null).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (response: any) => {
          if (response.callSuccess === '1' && response.jsonObject) {
            this.filingAllDetails = JSON.parse(JSON.stringify(response.jsonObject));
            this.filingDetails = JSON.parse(JSON.stringify(response.jsonObject));
            this.filingDetails = _.uniqBy(this.filingDetails, 'CONSOL_GROUP_KEY');
            this.filingEnityDetailInfo();
          }
        },
        (error: any) => {
          console.error('Error loading filing group info:', error);
        }
      );
  }

  filingEnityDetailInfo(): void {
    this.filingEnityDetails = JSON.parse(JSON.stringify(this.filingAllDetails));
    this.filingEnityDetails.unshift({
      FILER_LE_KEY: "",
      FILER_LE: "SELECT"
    });

    if (!this.isCreate) {
      // Edit mode: find and set the selected filer entity
      const matched = this.filingEnityDetails.find((p: any) => p.FILER_LE_KEY === this.rowData.FILER_LE_KEY);
      if (matched) {
        this.filerEntityEdit = matched;
        this.filerEntityEdit_backup = JSON.parse(JSON.stringify(matched));
      }
    } else {
      // Add mode: reset filer entity arrays
      this.filerEntity = [];
      this.selectedFilerEntityKeys = [];
      this.filerEntity_backup = [];
      // Initialize filtered data for searchable dropdown
      this.filerEntityFilteredData = this.filingEnityDetails.filter((item: any) => item.FILER_LE_KEY !== '');
    }
  }

  getStatus(): void {
    if (!this.grossIncome || !this.grossIncome.GIE_NAME) {
      return;
    }

    // 🔴 CRITICAL: Include ALL parameters with proper fallbacks (matching action code q7r80m)
    const params: any = {
      action_code: 'q7r80m',
      tax_year: this.filterParams?.tax_year || this.globalService?.tax_year || this.rowData?.TAX_YEAR || '',
      scenario: this.filterParams?.scenario || this.globalService?.scenario || this.rowData?.SCENARIO || '74',
      jcd_key: this.filterParams?.jcd_key || this.globalService?.jcd_key || this.rowData?.JCD_KEY || '250',
      jcd_ta_key: this.filterParams?.jcd_ta_key || this.globalService?.jcd_ta_key || this.rowData?.JCD_TA_KEY || '1250',
      activity_key: this.filterParams?.activity_key || this.globalService?.activity_key || this.rowData?.ACTIVITY_KEY || '1349',
      screen_key: this.filterParams?.screen_key || this.globalService?.screen_key || this.rowData?.SCREEN_KEY || '10765',
      screen_type: this.filterParams?.screen_type || this.globalService?.screen_type || this.rowData?.SCREEN_TYPE || 'grid',
      is_locked: this.filterParams?.is_locked || this.globalService?.is_locked || this.rowData?.IS_LOCKED || 'N',
      issue_id: this.filterParams?.issue_id || this.globalService?.issue_id || this.rowData?.ISSUE_ID || '',
      issue_short_desc: this.filterParams?.issue_short_desc || this.globalService?.issue_short_desc || this.rowData?.ISSUE_SHORT_DESC || '',
      issue_id_for_tx_save: this.filterParams?.issue_id_for_tx_save || this.globalService?.issue_id_for_tx_save || this.rowData?.ISSUE_ID_FOR_TX_SAVE || '',
      scenarioCode: this.filterParams?.scenarioCode || this.filterParams?.scenario_code || this.globalService?.scenarioCode || this.globalService?.scenario_code || 'RAF',
      is_issue_key_enabled: this.filterParams?.is_issue_key_enabled || this.globalService?.is_issue_key_enabled || 'N',
      filing_group_key: this.filterParams?.filing_group_key || this.globalService?.filing_group_key || this.rowData?.FILING_GROUP_KEY || '1',
      gie_name: this.grossIncome.GIE_NAME,
      sso_id: this.logged_in_user || this.USER_SETTINGS?.user?.sso_id || ''
    };

    this.jsonObjectFactory.getJSONObj(this.GENERAL_CONFIG.base_url + '/loadJsonObject', params, this.globalService, null, null).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (response: any) => {
          if (response.callSuccess === '1' && response.jsonObject) {
            // Remove duplicates from response using unique key combination
            const normalize = (value: any) => String(value ?? '').trim();
            const uniqueStatuses = response.jsonObject.filter((status: any, index: number, self: any[]) => {
              const statusKey = `${normalize(status.CODE_GROUP)}|${normalize(status.IS_LOCKED)}|${normalize(status.LOCK_DESC)}`;
              return index === self.findIndex((s: any) => 
                `${normalize(s.CODE_GROUP)}|${normalize(s.IS_LOCKED)}|${normalize(s.LOCK_DESC)}` === statusKey
              );
            });
            this.statusDetails = uniqueStatuses;
            this.statusDetails.unshift({
              CODE_GROUP: "",
              IS_LOCKED: "SELECT",
              LOCK_DESC: ""
            });

            // Set initial status value
            if (!this.isCreate) {
              const rowStatus = {
                CODE_GROUP: this.rowData?.CODE_GROUP ?? '',
                IS_LOCKED: this.rowData?.IS_LOCKED ?? '',
                LOCK_DESC: this.rowData?.LOCK_DESC ?? ''
              };

              let matchedStatus = this.statusDetails.find((status: any) =>
                normalize(status.CODE_GROUP) === normalize(rowStatus.CODE_GROUP) &&
                normalize(status.IS_LOCKED) === normalize(rowStatus.IS_LOCKED) &&
                normalize(status.LOCK_DESC) === normalize(rowStatus.LOCK_DESC)
              );

              if (!matchedStatus) {
                matchedStatus = this.statusDetails.find((status: any) =>
                  normalize(status.IS_LOCKED) === normalize(rowStatus.IS_LOCKED) &&
                  normalize(status.CODE_GROUP) === normalize(rowStatus.CODE_GROUP)
                );
              }

              if (!matchedStatus && (rowStatus.CODE_GROUP || rowStatus.IS_LOCKED || rowStatus.LOCK_DESC)) {
                const fallbackStatus = {
                  CODE_GROUP: rowStatus.CODE_GROUP,
                  IS_LOCKED: rowStatus.IS_LOCKED || 'SELECT',
                  LOCK_DESC: rowStatus.LOCK_DESC
                };
                // Check if fallbackStatus already exists in statusDetails before pushing
                // Compare by all three fields to prevent duplicates
                const fallbackExists = this.statusDetails.find((status: any) =>
                  normalize(status.CODE_GROUP) === normalize(fallbackStatus.CODE_GROUP) &&
                  normalize(status.IS_LOCKED) === normalize(fallbackStatus.IS_LOCKED) &&
                  normalize(status.LOCK_DESC) === normalize(fallbackStatus.LOCK_DESC)
                );
                // Also check if a status with the same LOCK_DESC already exists (since LOCK_DESC is the displayed value)
                // This prevents duplicate display values in the dropdown
                const lockDescExists = fallbackStatus.LOCK_DESC && normalize(fallbackStatus.LOCK_DESC) !== '' && normalize(fallbackStatus.LOCK_DESC) !== 'SELECT'
                  ? this.statusDetails.find((status: any) =>
                      normalize(status.LOCK_DESC) === normalize(fallbackStatus.LOCK_DESC)
                    )
                  : null;
                if (!fallbackExists && !lockDescExists) {
                  this.statusDetails.push(fallbackStatus);
                  matchedStatus = fallbackStatus;
                } else {
                  matchedStatus = fallbackExists || lockDescExists || fallbackStatus;
                }
              }

              this.status = matchedStatus ? matchedStatus : {
                CODE_GROUP: "",
                IS_LOCKED: "SELECT",
                LOCK_DESC: ""
              };
            } else {
              this.status = {
                CODE_GROUP: "",
                IS_LOCKED: "SELECT",
                LOCK_DESC: ""
              };
            }
            this.status_backup = JSON.parse(JSON.stringify(this.status));
            this.cdr.detectChanges();
          }
        },
        (error: any) => {
          console.error('Error loading status:', error);
        }
      );
  }

  onGrossIncomeChange(): void {
    this.getFillingGroupInfo();
    this.getStatus();
  }

  // Searchable multi-select dropdown methods
  filterFilerEntityDropdown(): void {
    if (!this.filingEnityDetails || this.filingEnityDetails.length === 0) {
      this.filerEntityFilteredData = [];
      return;
    }
    
    // Filter out the "SELECT" option (empty FILER_LE_KEY)
    let filtered = this.filingEnityDetails.filter((item: any) => item.FILER_LE_KEY !== '');
    
    // Apply search filter if search text exists
    if (this.filerEntitySearchText && this.filerEntitySearchText.trim() !== '') {
      const searchLower = this.filerEntitySearchText.toLowerCase().trim();
      filtered = filtered.filter((item: any) => {
        const displayValue = item.FILER_LE || '';
        return displayValue.toLowerCase().includes(searchLower);
      });
    }
    
    this.filerEntityFilteredData = filtered;
  }
  
  onFilerEntitySearchChange(): void {
    this.filterFilerEntityDropdown();
  }
  
  toggleFilerEntityDropdown(): void {
    this.filerEntityDropdownOpen = !this.filerEntityDropdownOpen;
    if (this.filerEntityDropdownOpen) {
      this.filerEntitySearchText = '';
      this.filterFilerEntityDropdown();
    }
  }
  
  closeFilerEntityDropdown(): void {
    if (this.filerEntityDropdownOpen) {
      this.filerEntityDropdownOpen = false;
      this.filerEntitySearchText = '';
      this.cdr.detectChanges();
    }
  }
  
  isFilerEntitySelected(filerLeKey: string): boolean {
    // 🔴 CRITICAL: Convert both to strings for consistent comparison
    // FILER_LE_KEY might be number or string, so normalize both sides
    const key = String(filerLeKey);
    return this.selectedFilerEntityKeys.some(k => String(k) === key);
  }
  
  /**
   * Check if the click event target is a checkbox input
   * Used to prevent double-toggle when clicking checkbox
   */
  isCheckboxClick(event: any): boolean {
    const target = event.target as HTMLElement;
    return target && target.tagName === 'INPUT' && (target as HTMLInputElement).type === 'checkbox';
  }

  toggleFilerEntitySelection(option: any): void {
    if (option.FILER_LE_KEY === '') {
      return; // Don't allow selecting the "SELECT" option
    }
    
    // Convert to string for consistent comparison
    const key = String(option.FILER_LE_KEY);
    const index = this.selectedFilerEntityKeys.findIndex(k => String(k) === key);
    if (index > -1) {
      // Unselect
      this.selectedFilerEntityKeys.splice(index, 1);
    } else {
      // Select
      this.selectedFilerEntityKeys.push(key);
    }
    // 🔴 CRITICAL: Trigger change detection to update the display
    this.cdr.detectChanges();
  }
  
  selectAllFilerEntities(): void {
    // Select all items in filtered data (excluding empty key)
    this.filerEntityFilteredData.forEach((item: any) => {
      if (item.FILER_LE_KEY !== '') {
        const key = String(item.FILER_LE_KEY);
        // Check if already selected using string comparison
        const isSelected = this.selectedFilerEntityKeys.some(k => String(k) === key);
        if (!isSelected) {
          this.selectedFilerEntityKeys.push(key);
        }
      }
    });
    // 🔴 CRITICAL: Trigger change detection to update the display
    this.cdr.detectChanges();
  }
  
  unselectAllFilerEntities(): void {
    // Unselect all items in filtered data
    this.filerEntityFilteredData.forEach((item: any) => {
      const key = String(item.FILER_LE_KEY);
      const index = this.selectedFilerEntityKeys.findIndex(k => String(k) === key);
      if (index > -1) {
        this.selectedFilerEntityKeys.splice(index, 1);
      }
    });
    // 🔴 CRITICAL: Trigger change detection to update the display
    this.cdr.detectChanges();
  }
  
  getSelectedFilerEntitiesDisplay(): string {
    if (!this.selectedFilerEntityKeys || this.selectedFilerEntityKeys.length === 0) {
      return '';
    }
    
    // 🔴 CRITICAL: Convert both to strings for consistent comparison
    // FILER_LE_KEY might be number or string, so normalize both sides
    const selectedKeysAsStrings = this.selectedFilerEntityKeys.map(k => String(k));
    const selectedEntities = this.filingEnityDetails.filter((item: any) => {
      if (!item.FILER_LE_KEY || item.FILER_LE_KEY === '') {
        return false;
      }
      const itemKey = String(item.FILER_LE_KEY);
      return selectedKeysAsStrings.includes(itemKey);
    });
    
    if (selectedEntities.length === 0) {
      return '';
    }
    
    // 🔴 CRITICAL: Display format - show entity name for 1, count for multiple
    if (selectedEntities.length === 1) {
      return selectedEntities[0].FILER_LE;
    } else {
      // Show count when more than 1 is selected (e.g., "2 selected", "3 selected")
      return `${selectedEntities.length} selected`;
    }
  }

  reset(): void {
    if (this.isCreate) {
      // Add mode: Clear all fields
      this.filerEntity = [];
      this.selectedFilerEntityKeys = [];
      this.grossIncome = {
        GIE_NAME: '',
        GIE_SHORT_DESC: 'SELECT'
      };
      this.status = {
        CODE_GROUP: "",
        IS_LOCKED: "SELECT",
        LOCK_DESC: ""
      };
      // Reset searchable dropdown state
      this.filerEntityDropdownOpen = false;
      this.filerEntitySearchText = '';
      this.filerEntityFilteredData = this.filingEnityDetails.filter((item: any) => item.FILER_LE_KEY !== '');
    } else {
      // Edit mode: Restore ONLY status if changed
      const statusChanged =
        this.status?.CODE_GROUP !== this.status_backup?.CODE_GROUP ||
        this.status?.IS_LOCKED !== this.status_backup?.IS_LOCKED ||
        this.status?.LOCK_DESC !== this.status_backup?.LOCK_DESC;
      if (statusChanged) {
        // 🔴 CRITICAL: Find matching object from statusDetails array (not create new object)
        // Angular's [(ngModel)] with [ngValue] requires object reference matching
        const normalize = (value: any) => String(value ?? '').trim();
        const matchedStatus = this.statusDetails.find((status: any) =>
          normalize(status.CODE_GROUP) === normalize(this.status_backup?.CODE_GROUP) &&
          normalize(status.IS_LOCKED) === normalize(this.status_backup?.IS_LOCKED) &&
          normalize(status.LOCK_DESC) === normalize(this.status_backup?.LOCK_DESC)
        );
        
        if (matchedStatus) {
          // ✅ Found matching object - use it directly (reference match)
          this.status = matchedStatus;
        } else {
          // Fallback: If no match found, use backup (shouldn't happen normally)
          this.status = JSON.parse(JSON.stringify(this.status_backup));
        }
      }
    }
    this.cdr.detectChanges();
  }

  save(): void {
    // Prevent double-click
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    // 🔴 CRITICAL: Sync selectedFilerEntityKeys to filerEntity array for add mode validation and save
    // In add mode, multi-select dropdown uses selectedFilerEntityKeys, but validation and save need filerEntity array
    if (this.isCreate && this.selectedFilerEntityKeys && this.selectedFilerEntityKeys.length > 0) {
      // Convert selected keys to strings for comparison (FILER_LE_KEY might be number or string)
      const selectedKeysAsStrings = this.selectedFilerEntityKeys.map(k => String(k));
      this.filerEntity = this.filingEnityDetails.filter((item: any) => {
        if (!item.FILER_LE_KEY || item.FILER_LE_KEY === '') {
          return false;
        }
        const itemKey = String(item.FILER_LE_KEY);
        return selectedKeysAsStrings.includes(itemKey);
      });
    } else if (this.isCreate) {
      // Clear filerEntity if no items selected in add mode
      this.filerEntity = [];
    }

    // Validation
    if ((this.isCreate &&
      (this.grossIncome && (this.grossIncome.GIE_NAME == undefined ||
        this.grossIncome.GIE_NAME == null || this.grossIncome.GIE_NAME == "") ||
        this.status.LOCK_DESC == undefined || this.status.IS_LOCKED == 'SELECT' ||
        (this.filerEntity && this.filerEntity.length === 0))) ||
      (!this.isCreate && (this.status.LOCK_DESC == undefined || this.status.IS_LOCKED == 'SELECT'))) {
      this.gtwSnackbarService.openSnackBar('Please select/enter all the required fields.', 'gtw-snackbar--red');
      this.isSaveClicked = false;
      this.crudLoading = false;
      this.cdr.detectChanges();
      return;
    }

    if (this.isCreate) {
      // Check if any filer entity is SELECT
      let flag1 = false;
      this.filerEntity.forEach((fEntityInfo: any) => {
        if (fEntityInfo.FILER_LE == 'SELECT') {
          flag1 = true;
        }
      });

      if (flag1) {
        this.gtwSnackbarService.openSnackBar('Please select/enter all the required fields.', 'gtw-snackbar--red');
        this.isSaveClicked = false;
        this.crudLoading = false;
        this.cdr.detectChanges();
        return;
      }
    }

    // Check for duplicates
    if (this.isCreate) {
      let flag = false;
      this.filerEntity.forEach((fEntityInfo: any) => {
        if (_.find(this.gotExistingData, {
          'GIE_SHORT_DESC': this.grossIncome.GIE_SHORT_DESC,
          'FILER_LE': fEntityInfo.FILER_LE
        })) {
          flag = true;
        }
      });
      if (flag) {
        this.gtwSnackbarService.openSnackBar('Apportionment type setup already exist.', 'gtw-snackbar--red');
        this.isSaveClicked = false;
        this.crudLoading = false;
        this.cdr.detectChanges();
        return;
      }
    } else {
      // Edit mode: Check duplicate using filerEntityEdit (single object)
      if (this.filerEntityEdit && this.filerEntityEdit.FILER_LE && this.filerEntityEdit.FILER_LE !== 'SELECT') {
        const existing = _.find(this.gotExistingData, {
          'GIE_SHORT_DESC': this.grossIncome.GIE_SHORT_DESC,
          'FILER_LE': this.filerEntityEdit.FILER_LE,
          'LOCK_DESC': this.status.LOCK_DESC
        });
        // Only show error if it's a different record (not the current one being edited)
        if (existing && existing.ADJ_KEY !== this.rowData.ADJ_KEY) {
          this.gtwSnackbarService.openSnackBar('Apportionment type setup already exist.', 'gtw-snackbar--red');
          this.isSaveClicked = false;
          this.crudLoading = false;
          this.cdr.detectChanges();
          return;
        }
      }
    }

    // Build save data
    const clobSettingsObj: any = {};
    clobSettingsObj['sso_id'] = this.logged_in_user || this.USER_SETTINGS?.user?.sso_id || '';
    clobSettingsObj['scenario'] = this.filterParams?.scenario || this.globalService?.scenario || '';
    clobSettingsObj["TAX_YEAR"] = this.filterParams?.tax_year || this.globalService?.tax_year || '';

    const returnObj: any = {};
    returnObj["TAX_YEAR"] = this.filterParams?.tax_year || this.globalService?.tax_year || '';
    returnObj['scenario'] = this.filterParams?.scenario || this.globalService?.scenario || '';
    returnObj['FILER_LE_KEY'] = this.isCreate ? _.map(this.filerEntity, 'FILER_LE_KEY').join() : this.filerEntityEdit.FILER_LE_KEY;
    returnObj['GIE_NAME'] = this.grossIncome.GIE_NAME;
    returnObj['CODE_GROUP'] = this.status.CODE_GROUP;
    returnObj['IS_LOCKED'] = this.status.IS_LOCKED;
    returnObj['LOCK_DESC'] = this.status.LOCK_DESC;
    returnObj['ADJ_KEY'] = this.isCreate ? null : this.rowData.ADJ_KEY;
    returnObj['FILING_GROUP_KEY'] = this.filterParams?.filing_group_key || this.globalService?.filing_group_key || '';
    returnObj["OPERATION_TYPE"] = this.isCreate ? 'I' : 'U';

    const clobDataDetails: any[] = [];
    clobDataDetails.push(returnObj);

    const message = "Apportionment type configuration has been saved/updated";

    // Call service to save
    this.apprConfigService.saveAppConfig(
      this.GENERAL_CONFIG.base_url,
      clobSettingsObj,
      clobDataDetails,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.errorMessage && response.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
